/** The card body: the List cards, or the Month/Week grid. */
import type { ReactElement } from 'react';

import type { WeekStartsOn } from './workshops-calendar-dates';
import { listWindowThrough } from './workshops-calendar-period';
import { WorkshopsCalendarGrid } from './WorkshopsCalendarGrid';
import { WorkshopsCalendarListView } from './WorkshopsCalendarListView';
import type {
  CalendarView,
  DayClickHandler,
  WorkshopChip,
  WorkshopClickHandler,
} from './workshopsCalendarTypes';

/** A chip drawn in its sub-region colour even when it is the viewer's — the
 *  grid's "mine" highlight replaces that colour outright. */
const withoutMine = (w: WorkshopChip): WorkshopChip => (w.mine ? { ...w, mine: false } : w);

export interface WorkshopsCalendarBodyProps {
  view: CalendarView;
  /** What to draw: the grid's workshops, or the List's upcoming ones in date order. */
  workshops: WorkshopChip[];
  anchor: Date;
  /** List only: the message when there is nothing to draw. */
  emptyLabel?: string;
  /** List only: whether the cards carry the viewer's role tag. */
  showTag?: boolean;
  today?: Date;
  weekStartsOn: WeekStartsOn;
  showPacing?: boolean;
  highlightMineInGrid?: boolean;
  onDayClick?: DayClickHandler;
  onWorkshopClick?: WorkshopClickHandler;
}

export function WorkshopsCalendarBody({
  view,
  workshops,
  anchor,
  emptyLabel,
  showTag,
  today,
  weekStartsOn,
  showPacing,
  highlightMineInGrid = true,
  onDayClick,
  onWorkshopClick,
}: WorkshopsCalendarBodyProps): ReactElement {
  if (view === 'List') {
    return (
      <WorkshopsCalendarListView
        workshops={workshops}
        emptyLabel={emptyLabel}
        showTag={showTag}
        through={listWindowThrough(today ?? new Date())}
        showPacing={showPacing}
        onWorkshopClick={onWorkshopClick}
      />
    );
  }
  return (
    <WorkshopsCalendarGrid
      workshops={highlightMineInGrid ? workshops : workshops.map(withoutMine)}
      view={view}
      anchor={anchor}
      today={today}
      weekStartsOn={weekStartsOn}
      onDayClick={onDayClick}
      onWorkshopClick={onWorkshopClick}
    />
  );
}
